<template>
  <div>
    <modal :id="modalId" :isConfirm="true" direction="ttb" size="20%" @on-confirm-status="onConfirm" />
    <div
      ref="cameraswrapper"
      @dragenter="onDragEnter"
      @dragleave="onDragLeave"
      @drop="onDrop"
      :name="fieldName"
      :class="[
        subFormView,
        {
          'core-cameras--drag-over': hasDragEntered,
          'justify-content-end': files.length < 1,
        },
        'core-cameras form-control d-flex align-items-center',
      ]"
      :disabled="disabledProps"
    >
      <template v-if="files.length < 1 && !readOnly">
        <el-button
          v-if="subFormView === 'default'"
          size="mini"
          icon="el-icon-upload"
          @click="$refs.input.click()"
          :title="$i18n.t('cseregomb')"
        ></el-button>
        <span v-else class="core-cameras__label">{{ $i18n.t('form_field_cameras_area_label') }}</span>
      </template>

      <transition-group v-else class="core-cameras__row w-100" name="core-cameras" tag="span" :key="componentRefresh">
        <div class="core-cameras__col" v-for="file in files" :key="file.key">
          <div v-if="subFormView === 'default'" class="w-100">
            <div class="d-flex justify-content-between align-items-center">
              <div class="cameras-image-title">
                <a :href="downloadUrl(file)" target="_blank">{{ shortFilename(file.name) }}</a>
              </div>
              <div class="btn-group close" role="group">
                <el-button
                  size="mini"
                  icon="el-icon-upload"
                  @click="changeFile(file)"
                  :title="$i18n.t('cseregomb')"
                ></el-button>
                <el-popconfirm
                  :title="$i18n.t('are_you_sure')"
                  :confirm-button-text="$i18n.t('Yes')"
                  :cancel-button-text="$i18n.t('No')"
                  cancel-button-type="Primary"
                  @confirm="confirmRemove(file.key)"
                >
                  <el-button slot="reference" size="mini" icon="el-icon-delete" :disabled="readOnly"></el-button>
                </el-popconfirm>
              </div>
            </div>
          </div>

          <div v-else class="core-cameras__image-wrapper">
            <div class="d-flex justify-content-between">
              <div class="core-cameras__image-title">{{ file.name }}</div>
              <div class="btn-group close" role="group">
                <button
                  :disabled="readOnly"
                  type="button"
                  :title="$i18n.t('cseregomb')"
                  class=""
                  @click="changeFile(file)"
                >
                  <span aria-hidden="true">&#8660;</span>
                </button>
                <button :disabled="readOnly" type="button" class="" @click="confirmRemove(file.key)">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>
            <div class="progress" v-if="uploadProgress[file.key]">
              <div class="progress-bar" role="progressbar" :style="{ width: `${uploadProgress[file.key].progress}%` }">
                {{ uploadProgress[file.key].progress }}%
              </div>
            </div>
            <!-- @todo delete temp data pjax 0 after new layout -->
            <div v-if="file.loading">Loading...</div>
            <div v-else-if="file.error" class="alert alert-danger dangenClass" role="alert">
              {{ $i18n.t('Download error') }}
            </div>
            <a
              v-else
              :href="downloadUrl(file)"
              download
              data-pjax="0"
              class="core-cameras__download-container"
              :class="{ nonDisabled: disabledProps }"
            >
              <img
                v-if="file.id && file.type.includes('image')"
                class="core-cameras__image img-responsive nonDisabled"
                :src="downloadUrl(file)"
              />
              <span v-if="!file.type.includes('image')" class="material-icons core-cameras__save-file nonDisabled"
                >cloud_download</span
              >
            </a>
          </div>
        </div>
      </transition-group>

      <div v-if="multiSelect && files.length > 0 && !readOnly" class="input-bottom-banner">
        {{ $i18n.t('form_field_cameras_area_label') }}
      </div>
      <input
        ref="input"
        :title="$i18n.t('form_field_cameras_area_label')"
        class="core-cameras__input"
        :accept="acceptTypes"
        type="file"
        v-if="!readOnly"
        v-on="$listeners"
        @change="onChange"
        :multiple="multiSelect"
      />
    </div>
  </div>
</template>

<script>
import Modal from '../../modal/Modal'
import file from '../../../utils/file'
import notify from '../../../utils/mixin-notify'
import axios from '../../../axiosClient'
import documentUpload from '@/utils/document-upload'
import { get, isEmpty } from 'lodash'

/**
 * @class Cameras
 *
 * @description This component renders a file uploader interface
 */
export default {
  inheritAttrs: false,
  name: 'cameras',
  components: {
    Modal,
  },
  props: {
    path: {},
    field: {
      type: Object,
    },
    value: {
      type: [String, Array],
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
    name: {
      type: String,
      required: true,
    },
    subformId: {
      type: String,
    },
    disabledProps: {
      type: Boolean,
    },
    readOnly: {
      type: Boolean,
    },
    uploadMetas: {
      default: () => ({}),
    },
    infoUrl: {
      default: '/api/DocumentStorages',
    },
    maxSize: {
      type: Number,
      default: null,
    },
    filter: {
      default: () => ({}),
    },
    fileListOID: {
      default: 'DocumentStorage_find',
    },
    accept: {
      default: '*/*',
    },
    quality: {
      type: Number,
      default: null,
    },
  },
  mixins: [file, notify, documentUpload],
  watch: {
    files: {
      deep: true,
      handler: async function (value) {
        if (!Array.isArray(value)) return
        const fileIds = value ? value.map((file) => file.id) : []
        this.$emit('form-item-changed', { value: fileIds })
      },
    },
  },
  computed: {
    fieldName() {
      return this.field ? this.field.name : this.name
    },
    multiSelect() {
      return this.multiple || this.field.multiselect
    },
    subFormView() {
      return this.$get('subformView')
    },
  },
  methods: {
    onChange($event) {
      if ($event.target.files.length > 0) {
        Array.from($event.target.files).forEach(this.processFile)
      }
    },
    downloadUrl(file) {
      if (!file) return
      if (file.isStatic || file.isTechnical) {
        return this.downloadUrlStatic(file.id)
      } else {
        return this.getDownloadUrl(file.id)
      }
    },
    uploadFile(formData, file, index) {
      const config = this.getAxiosConfig(file)

      axios({ method: 'post', ...config })
        .then(instance => instance.post(this.uploadURL, formData, config))
        .then((response) => response.data)
        .then((data) => {
          if (data.name && data.message) {
            data.message.forEach((msg) => {
              noty.text = msg
              this.showNotification(noty)
            })
            return
          }
          this.$set(this.files, this.files.indexOf(file), {
            ...file,
            ...data,
          })
        })
        .catch((error) => {
          if (error.message) {
            const noty = {
              text: 'cameras_upload_error ${errorText} ${fileName}',
              params: {
                errorText: error.message,
                fileName: this.files[index].name,
              },
              type: 'error',
              duration: 10,
            }
            this.fileId = this.files[index].key
            this.onRemove()
            this.showNotification(noty)
          }
        })
    },
    processFile(file) {
      const newFile = this.renameFile(file, file.name.replace(/[,*+?^#@&<>%!+"'=${}() |[\]\\]/g, ''))
      const key = `${encodeURI(newFile.name)}-${newFile.size}-${newFile.lastModified}`
      const reader = new FileReader()

      const addedFile = this.files.find((file) => file.key === key)
      if (this.files.includes(addedFile)) return

      if (!this.isFileValid(file)) return

      if (!this.multiSelect) this.files = []

      const oldId = get(this.oldFile, 'id')
      this.files.push({
        name: newFile.name,
        size: file.size,
        type: file.type,
        oldId: oldId ? JSON.stringify(oldId) : '',
        key,
      })

      if (oldId) {
        this.files = this.files.filter((file) => file.id !== oldId)
      }
      this.oldFile = {}

      reader.onloadstart = () => {
        this.files.forEach((file) => {
          if (file.key === key) {
            this.$set(this.uploadProgress, file.key, {
              progress: 0,
              finished: false,
            })
          }
        })
      }

      reader.onload = (ev) => {
        this.files.forEach((file, index) => {
          if (file.key === key) {
            let formData = new FormData()

            const data = new Blob([ev.target.result])

            formData.append('data', data)
            formData.append('metas', JSON.stringify(this.uploadMetas))
            formData.append('isTechnical', this.field.isTechnical || false)
            formData.append('isStatic', this.field.isStatic || false)
            formData.append('refField', this.field.name)
            formData.append('formId', this.subformId)

            Object.keys(file).forEach((key) => {
              if (!formData.has(key)) {
                formData.append(key, file[key])
              }
            })

            if (this.isImageType(file.type)) {
              this.getImageSize(data).then((img) => {
                formData.append(
                  'dimensions',
                  JSON.stringify({
                    width: img.width,
                    height: img.height,
                  })
                )
                this.uploadFile(formData, file, index)
              })
            } else {
              this.uploadFile(formData, file, index)
            }
          }
        })
      }
      reader.readAsArrayBuffer(file)
    },
  },
  async created() {
    if (!isEmpty(this.filter) && !isEmpty(this.value)) {
      const { body: files } = await this.findDocuments(this.filter)

      if (!Array.isArray(files)) return

      const preparedFiles = await this.checkFiles(files)

      if (!isEmpty(preparedFiles)) {
        this.files = preparedFiles
      }
    }
  },
}
</script>

<style lang="scss">
.dangenClass {
  width: 100% !important;
}
.nonDisabled {
  pointer-events: all;
}
.core-cameras {
  position: relative;
  z-index: 1;
  padding: 12px;
  overflow: hidden;
  background: lighten($brandColor, 20%);
  font-size: $font-size;
  &__col.default {
    flex-direction: column;
  }
  &.form-control {
    height: auto;
  }
  &.default {
    min-height: 40px;
    padding: 0;
    border-radius: $default-border-radius;
  }
  & .progress .progress-bar {
    min-width: 2em;
  }
  &__col {
    width: 100%;
    display: flex;
    padding: 4px;
    padding-inline-start: 10px;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
    transform: translateY(30px) scale(0);
  }
  &-enter-active,
  &-leave-active {
    transition: all 0.3s;
  }
  &__label {
    display: inline-block;
    width: 100%;
    text-align: center;
    line-height: 20px;
  }
  &__input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    font-size: 100em;
    z-index: 2;
    opacity: 0.01;
  }
  &__image-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: center;
    position: relative;
    z-index: 3;
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.75);
    border-radius: 0.286em;
  }
  &__image-title {
    display: inline-block;
    margin: 0 20px;
    padding: 6px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__image {
    padding: 6px;
    border-radius: 10px;
    transition: all 0.4s ease-in-out;
    height: 100%;
    width: 100%;
    object-fit: contain;
    &:hover {
      transform: scale(1.02);
      background: #2b2a2a4a;
    }
  }
  &__remove {
    width: 10%;
    span {
      width: 100%;
      height: 100%;
    }
  }
  &__save-file {
    font-size: 70px !important;
    margin: 1rem auto;
  }
  &__download-container {
    position: relative;
    overflow: hidden;
    display: flex;
    padding: 5px;
    justify-content: center;
    align-items: center;
    margin: auto;
    max-height: 200px;
  }
}
.cameras-image-title {
  & a {
    color: $color-black;
  }
}
.d-flex > .close {
  display: inline-flex !important;
}
@media all and (min-width: 40em) {
  .core-cameras__col {
    z-index: 4;
    width: 50%;
  }
  .core-cameras.default .core-cameras__col {
    width: 100%;
  }
}
@media all and (min-width: 60em) {
  .core-cameras__col {
    width: 33.33%;
  }
  .core-cameras.default .core-cameras__col {
    width: 100%;
  }
}
.core-cameras--drag-over {
  .core-cameras__image-wrapper {
    z-index: 1;
  }
}
.core-cameras__remove {
  width: auto;
}
.input-bottom-banner {
  text-align: center;
  margin: 1rem auto 0;
}
.sub-form-item-wrapper {
  .core-cameras {
    height: 40px;
  }
}
</style>
<style lang="scss" scoped>
[disabled] {
  background: $background-color-readonly;
}
</style>
